/**
 * Deposit money that settled in a date range — required, not optional
 * (CLAUDE.md, step 9): the client routes rent and deposits to one operating
 * account and moves the deposit portion to escrow by hand, and this report is
 * the input to that step. Per-payment, not aggregated, so the bookkeeper can
 * identify exactly which settled payments' deposit slice to transfer.
 *
 * Fetched with `api()` directly rather than Refine's `useList`: the response
 * carries a server-computed `totalDepositAmount` alongside the rows, and the
 * data provider's list envelope only forwards `data`/`total` (a row count) —
 * so `useList` here would silently drop the one figure this report exists to
 * report. The alternative, summing `depositAmount` across rows in the
 * browser, is exactly what CLAUDE.md's step 9 rules out. The trend chart
 * below sums the same way — into day/week buckets, in JS, from rows the
 * server already returned — which is a display aggregation of numbers
 * already reported, not a second computation of the balance itself.
 */

import { useEffect, useState } from 'react';
import { api } from '../../../lib/api.js';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { ReportFilterBar } from '../../../components/ReportFilterBar.js';
import { TrendChart, type TrendPoint } from '../../../components/charts/TrendChart.js';
import { humanizeEnum } from '../../../components/Form.js';

interface DepositsCollectedRow {
  paymentId: string;
  leaseId: string;
  propertyName: string;
  unitLabel: string;
  settledAt: string;
  method: string;
  depositAmount: string;
}

interface DepositsCollectedResponse {
  data: DepositsCollectedRow[];
  total: number;
  totalDepositAmount: string;
}

const columns: Column<DepositsCollectedRow>[] = [
  { key: 'settled', header: 'Settled', render: (r) => r.settledAt.slice(0, 10) },
  { key: 'property', header: 'Property', render: (r) => r.propertyName },
  { key: 'unit', header: 'Unit', render: (r) => r.unitLabel },
  { key: 'method', header: 'Method', render: (r) => humanizeEnum(r.method) },
  {
    key: 'amount',
    header: 'Deposit collected',
    numeric: true,
    render: (r) => `$${r.depositAmount}`,
  },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

function money(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

const DAY_MS = 86_400_000;

/** One point per day if the window is short, per week if it's long enough
 *  that daily points would just be noise (a year of daily bars is unreadable
 *  either way) — bucket boundaries are relative to `from`, not calendar
 *  weeks, since there's no reason this needs to line up with Monday.
 *
 *  Label formatting is pinned to UTC below because these are date-only
 *  values sent as UTC midnight; `toLocaleDateString`'s default local-
 *  timezone rendering would print the day before the actual settlement date
 *  for every viewer west of UTC (metro Detroit, this app's whole userbase).
 *  The bucket math itself doesn't need this — only display formatting does. */
function buildTrend(rows: DepositsCollectedRow[], from: string, to: string): TrendPoint[] {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  const totalDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1);
  const bucketDays = totalDays > 45 ? 7 : 1;
  const bucketCount = Math.ceil(totalDays / bucketDays);

  const sums = new Map<number, number>();
  for (const row of rows) {
    const settled = new Date(`${row.settledAt.slice(0, 10)}T00:00:00Z`);
    const dayOffset = Math.round((settled.getTime() - start.getTime()) / DAY_MS);
    const bucketIndex = Math.min(bucketCount - 1, Math.max(0, Math.floor(dayOffset / bucketDays)));
    sums.set(bucketIndex, (sums.get(bucketIndex) ?? 0) + Number(row.depositAmount));
  }

  return Array.from({ length: bucketCount }, (_, i) => {
    const bucketStart = new Date(start.getTime() + i * bucketDays * DAY_MS);
    const value = sums.get(i) ?? 0;
    const dateLabel = bucketStart.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
    return {
      label: bucketDays === 1 ? dateLabel : `Week of ${dateLabel}`,
      value,
      displayValue: money(value),
    };
  });
}

export function DepositsCollected({
  ownerId,
  onOwnerChange,
  tenantId,
  onTenantChange,
}: {
  ownerId: string;
  onOwnerChange: (value: string) => void;
  tenantId: string;
  onTenantChange: (value: string) => void;
}) {
  const [propertyId, setPropertyId] = useState('');
  const [from, setFrom] = useState(firstOfMonthIso());
  const [to, setTo] = useState(todayIso());
  const [result, setResult] = useState<DepositsCollectedResponse | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const query = new URLSearchParams({ from, to });
    if (propertyId) query.set('propertyId', propertyId);
    if (ownerId) query.set('ownerId', ownerId);
    if (tenantId) query.set('tenantId', tenantId);
    api<DepositsCollectedResponse>(`/reports/deposits-collected?${query.toString()}`)
      .then((response) => {
        if (!cancelled) setResult(response);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, propertyId, ownerId, tenantId]);

  return (
    <div className="stack">
      <p className="muted">
        Security deposit money among payments that settled in this window — what to move to escrow,
        and how much. Grouped by payment, not by lease: a payment that also covers rent shows only
        its deposit slice here.
      </p>

      <ReportFilterBar
        propertyId={propertyId}
        onPropertyChange={setPropertyId}
        ownerId={ownerId}
        onOwnerChange={onOwnerChange}
        tenantId={tenantId}
        onTenantChange={onTenantChange}
        exportPath="/reports/deposits-collected"
        exportParams={{
          propertyId: propertyId || undefined,
          ownerId: ownerId || undefined,
          tenantId: tenantId || undefined,
          from,
          to,
        }}
      >
        <label className="row">
          <span className="muted">From</span>
          <input
            className="field__input"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label className="row">
          <span className="muted">To</span>
          <input
            className="field__input"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </label>
      </ReportFilterBar>

      {!isLoading && !error && result ? (
        <p className="muted">
          Total deposit money settled in this window: <strong>${result.totalDepositAmount}</strong>
        </p>
      ) : null}

      {!isLoading && !error && result && result.data.length > 0 ? (
        <div className="card">
          <p className="muted">Settled deposit money over the window above.</p>
          <TrendChart points={buildTrend(result.data, from, to)} colorVar="--accent" />
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={result?.data ?? []}
        rowKey={(row) => row.paymentId}
        isLoading={isLoading}
        error={error ?? undefined}
        emptyMessage="No deposit money settled in this window."
      />
    </div>
  );
}
